import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import fetchWithJWT from '../api/fetchWithJWT';
import { ONBOARDING_TO_PRODUCTS_KEY } from './StoreReady';

// Right after signup, Skip or Buy Plan (then payment) on the Store Ready popup:
// go to the product list, not the plan page or the add-product page.
const leaveForProducts = (navigate) => {
  if (!sessionStorage.getItem(ONBOARDING_TO_PRODUCTS_KEY)) return false;
  sessionStorage.removeItem(ONBOARDING_TO_PRODUCTS_KEY);
  navigate('/gift');
  return true;
};

// True when the seller has neither an active plan nor a paid subscription.
// False when they have one, aren't a business, or the check fails.
const needsPlan = async () => {
  const business_id = localStorage.getItem('business_id');
  if (!business_id) return false;
  try {
    const planResponse = await fetchWithJWT(`/business/plan/info?business_user_id=${business_id}`);
    const planData = await planResponse.json();
    const subscriptionResponse = await fetchWithJWT(`/subscription/info?business_user_id=${business_id}`);
    const subscriptionData = await subscriptionResponse.json();

    const hasActivePlan = planData.status === "success" &&
      planData.result &&
      planData.result.plan_name &&
      planData.result.plan_name.toLowerCase() !== 'unknown plan' &&
      planData.result.plan_status !== "none" &&
      planData.result.plan_status !== "created" &&
      planData.result.plan_status !== "cancelled" &&
      !planData.result.is_expired;

    const hasActiveSubscription = subscriptionData.status === "success" &&
      subscriptionData.result &&
      subscriptionData.result.has_subscription &&
      (subscriptionData.result.paid_count > 0 || subscriptionData.result.first_payment_discount_amount > 0) &&
      subscriptionData.result.status !== 'cancelled';

    return !hasActivePlan && !hasActiveSubscription;
  } catch (error) {
    console.error('Error checking plan/subscription status:', error);
    return false;
  }
};

const OnboardingSlider = ({ onClose, storeUrl }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      title: "Your Store Is Almost Live",
      subtitle: "Your store space is ready.",
      description: "You're just a few steps away from launching.",
      image: "/assets/img/image 7.png",
      bgColor: "#90CAF936",
      accentColor: "#1E5B3A"
    },
    {
      title: "Let's Create Your First Product!",
      subtitle: "This is where your store comes alive.",
      description: "Add your products, turn on sections, and get ready to sell.",
      points: [
        "Add product details and images",
        "Activate your store sections",
        "Publish the products"
      ],
      image: "/assets/img/Group 118.png",
      bgColor: "#90CAF936",
      accentColor: "#1E5B3A"
    },
    {
      title: "Create your payment gateway",
      subtitle: "Complete the process of the Payment gateway",
      points: [
        "Business Information",
        "Stakeholder Information",
        "Bank Account Details"
      ],
      image: "/assets/img/Group 125.png",
      bgColor: "#90CAF936",
      accentColor: "#1E5B3A"
    },
    {
      title: "Complete your store settings",
      subtitle: "Please add a product to complete your store setup",
     description: "Complete Store Appearance to enable:",
      points: [
        "Banner Slider",
        "Category Display",
        "Featured Products"
      ],
    //   description: `Get your store link ${storeUrl || 'storename.mytamilkart.com'}`,
      image: "/assets/img/Group 121.png",
      bgColor: "#90CAF936",
      accentColor: "#1E5B3A",
      isLast: true
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleGetStarted = async () => {
    // Mark onboarding as completed
    localStorage.setItem('hasSeenOnboarding', 'true');
    onClose();
    if (leaveForProducts(navigate)) return;
    if (await needsPlan()) {
      navigate('/purchase-plan');
      return;
    }
    // Navigate to add product page if user has active plan/subscription
    navigate('/gift_add');
  };

  const handleSkip = async () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    onClose();
    if (leaveForProducts(navigate)) return;
    if (await needsPlan()) navigate('/purchase-plan');
  };

  const slide = slides[currentSlide];

  return (
    <div 
      className="modal fade show" 
      style={{ 
        display: 'block', 
        backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 10000
      }}
    >
      <div 
        className="modal-dialog modal-dialog-centered" 
        style={{ 
          maxWidth: '900px',
          margin: '1.75rem auto'
        }}
      >
        <div 
          className="modal-content" 
          style={{ 
            borderRadius: '20px', 
            overflow: 'hidden',
            border: 'none',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          {/* Skip Button */}
          <button
            onClick={handleSkip}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'transparent',
              border: 'none',
              color: '#1E5B3A',
              fontSize: '1.1em',
              fontWeight: '600',
              cursor: 'pointer',
              zIndex: 10,
              padding: '8px 16px',
              borderRadius: '6px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(7, 101, 52, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            Skip
          </button>

          <div className="modal-body" style={{ padding: 0 }}>
            <div className="row" style={{ margin: 0, minHeight: '500px' }}>
              {/* Left Side - Visual */}
              <div 
                className="col-lg-5 col-md-12" 
                style={{ 
                  background: slide.bgColor,
                  padding: '60px 40px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div style={{
                  width: '100%',
                  maxWidth: '280px',
                  marginBottom: '20px',
                  animation: 'bounce 2s infinite'
                }}>
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                
                {currentSlide === 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <h6 style={{ 
                      color: '#666', 
                      fontSize: '1em',
                      fontWeight: '600',
                      marginTop: '20px'
                    }}>
                      Your store adventure starts here!
                    </h6>
                    <p style={{ 
                      color: 'rgba(0, 0, 0, 1)', 
                      fontSize: '0.9em',
                      marginTop: '10px'
                    }}>
                      Start your digital journey with MyTamilKart today
                    </p>
                  </div>
                )}

                {currentSlide === 1 && (
                  <div style={{ textAlign: 'center' }}>
                    <h6 style={{ 
                      color: '#666', 
                      fontSize: '1.1em',
                      fontWeight: '700',
                      marginTop: '20px'
                    }}>
                      Listing Products
                    </h6>
                  </div>
                )}

                {currentSlide === 3 && (
                  <div style={{ textAlign: 'center' }}>
                    <h6 style={{ 
                      color: '#666', 
                      fontSize: '1.1em',
                      fontWeight: '700',
                      marginTop: '20px'
                    }}>
                      You're almost done!
                    </h6>
                    <p style={{ 
                      color: '#999', 
                      fontSize: '0.9em',
                      marginTop: '10px'
                    }}>
                      Store Configuration
                    </p>
                  </div>
                )}
              </div>

              {/* Right Side - Content */}
              <div 
                className="col-lg-7 col-md-12" 
                style={{ 
                  padding: '60px 50px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <h2 style={{ 
                    fontSize: '2em',
                    fontWeight: '700',
                    marginBottom: '15px',
                    color: '#1a1a1a',
                    lineHeight: '1.2'
                  }}>
                    {slide.title.split(' ').map((word, i) => 
                      word === 'Almost' || word === 'Live' ? (
                        <span key={i} style={{ color: slide.accentColor }}>{word} </span>
                      ) : (
                        <span key={i}>{word} </span>
                      )
                    )}
                  </h2>

                  <h5 style={{ 
                    fontSize: '1.2em',
                    fontWeight: '600',
                    color: '#4a4a4a',
                    marginBottom: '10px'
                  }}>
                    {slide.subtitle}
                  </h5>

                  {slide.description && (
                    <p style={{ 
                      fontSize: '1em',
                      color: '#666',
                      marginBottom: '25px',
                      lineHeight: '1.6'
                    }}>
                      {slide.description}
                    </p>
                  )}

                  {slide.points && (
                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0,
                      marginTop: '25px'
                    }}>
                      {slide.points.map((point, index) => (
                        <li 
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '15px',
                            fontSize: '1em',
                            color: '#4a4a4a'
                          }}
                        >
                          <span style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: slide.accentColor,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px',
                            fontSize: '0.9em',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            ✓
                          </span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}

                  {slide.isLast && (
                    <div style={{
                      marginTop: '30px',
                      padding: '20px',
                      background: '#FDF9F2',
                      borderRadius: '10px',
                      border: '2px dashed #1E5B3A'
                    }}>
                      <p style={{ 
                        margin: 0,
                        fontSize: '0.95em',
                        color: '#666',
                        textAlign: 'center'
                      }}>
                        <strong style={{ color: '#1E5B3A' }}>Your Store Link:</strong><br/>
                        <a 
                          href={`https://${storeUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#1E5B3A',
                            textDecoration: 'none',
                            fontSize: '1.1em',
                            fontWeight: '600'
                          }}
                        >
                          {storeUrl || 'storename.mytamilkart.com'}
                        </a>
                      </p>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div style={{ marginTop: '40px' }}>
                  {/* Progress Dots */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '25px'
                  }}>
                    {slides.map((_, index) => (
                      <div
                        key={index}
                        style={{
                          width: currentSlide === index ? '32px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          background: currentSlide === index ? slide.accentColor : '#d1d5db',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => setCurrentSlide(index)}
                      />
                    ))}
                  </div>

                  {/* Buttons */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={handlePrev}
                      disabled={currentSlide === 0}
                      style={{
                        padding: '12px 24px',
                        fontSize: '1em',
                        fontWeight: '600',
                        borderRadius: '8px',
                        background: currentSlide === 0 ? '#e5e7eb' : 'white',
                        border: '2px solid #e5e7eb',
                        color: currentSlide === 0 ? '#9ca3af' : '#4b5563',
                        cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <ChevronLeft size={20} />
                      Previous
                    </button>

                    {slide.isLast ? (
                      <button
                        onClick={handleGetStarted}
                        style={{
                          padding: '12px 32px',
                          fontSize: '1em',
                          fontWeight: '600',
                          borderRadius: '8px',
                          background: slide.accentColor,
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 12px rgba(7, 101, 52, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(7, 101, 52, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(7, 101, 52, 0.3)';
                        }}
                      >
                        Get Started →
                      </button>
                    ) : (
                      <button
                        onClick={handleNext}
                        style={{
                          padding: '12px 32px',
                          fontSize: '1em',
                          fontWeight: '600',
                          borderRadius: '8px',
                          background: slide.accentColor,
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Next
                        <ChevronRight size={20} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @media (max-width: 768px) {
          .modal-dialog {
            margin: 0.5rem;
            max-width: calc(100% - 1rem) !important;
          }
          
          .col-lg-5, .col-lg-7 {
            padding: 30px 20px !important;
          }

          .row {
            min-height: auto !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingSlider;
